import type {FullContract} from '~/models/contract'
import {CONTRACT_TYPE_STEP_DEFINITIONS} from '~/composables/wizardStepDefinitions'
import {WIZARD_PAGE} from '~/models/wizardIds'
import {
    QUESTION_ENABLE_COMMENT_ALWAYS_OPTIONAL,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_NO,
    QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_YES,
    QUESTION_ENABLE_COMMENT_NEVER,
    QUESTION_OPTION_CONFIG_TEXT,
    QUESTION_TEMPLATE_TYPE_ENVIRONMENT_CONSIDERATION
} from "~/constants/questions";
import {
    GmDtoLibraryDtoProductionOrderDto,
    GmModelLibraryModelsContractsContractDetailModel,
    GMServicesCommonQuestionsQuestionDefinition,
    GMServicesCommonQuestionsQuestionTemplate
} from "~/gen/openapi/sblService";
import {useQuestionService} from "~/services/questionService";
import {
    ProductionTypeFelling,
    ProductionTypeFellingAndDelivery,
    ProductionTypeRoadDelivery
} from "~/constants/productionOrders";
import {convertStringPeriod} from "~/helpers/util";
import {hasAnswerType, isOptionalQuantity, normalizeYesNoValue, OPTION_YES, OPTION_NO} from "~/helpers/questions";

/**
 * Validation logic for environmentConsideration.
 * Kept as a named export for backward compatibility with helpers/environments.ts.
 * In component setup contexts, prefer using useValidations().isEnvironmentReportValid instead.
 */
export const isEnvironmentReportValid = (contract: FullContract, checkReadyForSign: Boolean, questionTemplates: Ref<Array<GMServicesCommonQuestionsQuestionTemplate | undefined>>): boolean => {
    const {getQuestionTemplateByType} = useQuestionService()
    const questionTemplate = computed(() => !!questionTemplates.value?.length ? getQuestionTemplateByType(questionTemplates.value, QUESTION_TEMPLATE_TYPE_ENVIRONMENT_CONSIDERATION, contract) : {})

    if (!questionTemplate.value?.QuestionDefinitions?.length) {
        return true
    }

    const isAllRequiredQuestionsAndCommentsAnswered = questionTemplate.value?.QuestionDefinitions?.every((questionDefinition: GMServicesCommonQuestionsQuestionDefinition) => {
        if (!hasAnswerType(questionDefinition)) {
            return true
        }
        const questionResponse = contract.EnvironmentConsideration?.QuestionResponses?.find((response) => response.QuestionDefinitionId === questionDefinition.Id)

        const minimumCommentLengthOk = () => {
            const commentMinChars = questionDefinition.CommentMinChars
            const commentLength = questionResponse?.Comment?.length || 0
            if (commentMinChars) {
                return commentLength >= commentMinChars
            }
            return true
        }

        const commentIsValid = () => {

            switch (questionDefinition.EnableComment) {
                // @ts-ignore
                case QUESTION_ENABLE_COMMENT_NEVER:
                    return true
                // @ts-ignore
                case QUESTION_ENABLE_COMMENT_ALWAYS_OPTIONAL:
                    return true
                // @ts-ignore
                case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_YES:
                    return normalizeYesNoValue(questionResponse?.Value) === OPTION_YES ? minimumCommentLengthOk() : true
                // @ts-ignore
                case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED_IF_NO:
                    return normalizeYesNoValue(questionResponse?.Value) === OPTION_NO ? minimumCommentLengthOk() : true
                // @ts-ignore
                case QUESTION_ENABLE_COMMENT_ALWAYS_REQUIRED:
                    return minimumCommentLengthOk()
                default:
                    return true
            }
        }

        // text questions only check comment
        if (questionDefinition.OptionConfig === QUESTION_OPTION_CONFIG_TEXT) {
            if (questionDefinition.Required) {
                return !!questionResponse?.Comment && minimumCommentLengthOk()
            }
            return true
        } else if (checkReadyForSign && !isOptionalQuantity(questionDefinition)) {
            if (questionResponse?.Value === undefined || !questionResponse?.Value?.toString()?.length) {
                return false
            }
        }

        if (questionDefinition.Required) {
            return commentIsValid() && !!questionResponse?.Value
        }

        return commentIsValid()
    })

    return !!isAllRequiredQuestionsAndCommentsAnswered
}


export const hasDuplicateProductionTypes = (productionOrders: GmDtoLibraryDtoProductionOrderDto[] | undefined | null) => {
    if (productionOrders && productionOrders?.length >= 2) {
        if (productionOrders.some((po) => po.ProductionTypeCode === ProductionTypeFellingAndDelivery)) {
            return true
        }

        const types = productionOrders?.map((po) => po.ProductionTypeCode)
        return new Set(types).size !== types?.length
    }
    return false
}

export const validateProductionTypes = (productionOrders: GmDtoLibraryDtoProductionOrderDto[] | undefined | null) => {
    if(productionOrders && productionOrders?.length >= 1) {
        const firstProductionTypeCode = productionOrders[0]?.ProductionTypeCode;
    
        if(productionOrders && productionOrders?.length >= 2) {
            const secondProductionTypeCode = productionOrders[1]?.ProductionTypeCode;
            if(!firstProductionTypeCode || !secondProductionTypeCode) {
                return 'ENTREPRENEUR_ERROR_NO_TYPE';
            }
            else if(firstProductionTypeCode === ProductionTypeFellingAndDelivery || secondProductionTypeCode === ProductionTypeFellingAndDelivery) {
                return 'ENTREPRENEUR_ERROR_ONLY_ONE_FELLING_AND_DELIVERY';
            } else if(firstProductionTypeCode === secondProductionTypeCode){
                return 'ENTREPRENEUR_ERROR_SAME_TYPE';
            }
            return;
        } 
        
        if(firstProductionTypeCode === ProductionTypeFelling) {
            return 'ENTREPRENEUR_ERROR_NO_DELIVERY';
        }
        else if(firstProductionTypeCode === ProductionTypeRoadDelivery) {
            return 'ENTREPRENEUR_ERROR_NO_FELLING';
        }
        else if(!firstProductionTypeCode) {
            return 'ENTREPRENEUR_ERROR_NO_TYPE'
        }
    }
    return '';
}

export const contractTypeShouldHaveEnvironmentReport = (contractType: number) => {
    const steps = CONTRACT_TYPE_STEP_DEFINITIONS[contractType] ?? []
    return steps.some((step) => step.Page === WIZARD_PAGE.ENVIRONMENT_REPORT)
}

export const hasExpiredPeriod = (contractDetail: GmModelLibraryModelsContractsContractDetailModel): boolean => {
    if (!contractDetail.Period || contractDetail.Period.length < 6) {
        return false
    }

    const {Year, LoggingMonth} = convertStringPeriod(contractDetail.Period)
    if (!LoggingMonth) return false

    const now = new Date()
    const currentYear = now.getFullYear()
    const currentMonth = now.getMonth() + 1

    return (
        Year < currentYear ||
        (Year === currentYear && LoggingMonth < currentMonth)
    )
}

export const hasExpiredPeriods = (contractDetails: GmModelLibraryModelsContractsContractDetailModel[]): boolean => {
    return contractDetails.some((detail) => hasExpiredPeriod(detail))
}